/**
 * PDF Preview Modal Component
 * Shows a PDF preview in an embedded viewer with download option
 * Supports both URL-based and API-fetched PDF previews
 */
import { useState, useEffect, useCallback } from "react";
import { X, FileText, Download, Loader2, AlertCircle, Maximize2, Minimize2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const PDFPreviewModal = ({
  // Legacy props for URL-based preview
  pdfPreviewUrl,
  quotationCode,
  closePdfPreview,
  handleDownloadPDF,
  // New props for API-based preview
  isOpen,
  onClose,
  quotationId
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [quotationInfo, setQuotationInfo] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Determine which mode we're in
  const isApiMode = quotationId && !pdfPreviewUrl;
  const shouldShow = isApiMode ? isOpen : !!pdfPreviewUrl;

  const fetchPDFPreview = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get(`${API}/quotations/${quotationId}/pdf-preview`);
      
      if (response.data.success) {
        setPdfData(response.data.pdf_base64);
        setQuotationInfo(response.data.quotation);
      } else {
        setError("No se pudo generar el preview");
      }
    } catch (err) {
      console.error("Error fetching PDF preview:", err);
      setError(err.response?.data?.detail || "Error al cargar el preview");
    } finally {
      setLoading(false);
    }
  }, [quotationId]);

  useEffect(() => {
    if (isApiMode && isOpen && quotationId) {
      fetchPDFPreview();
    } else if (!isOpen) {
      // Reset state when modal closes
      setPdfData(null);
      setQuotationInfo(null);
      setError(null);
      setIsFullscreen(false);
    }
  }, [isOpen, quotationId, isApiMode, fetchPDFPreview]);

  const handleApiDownload = () => {
    if (!pdfData) return;
    
    try {
      // Convert base64 to blob
      const byteCharacters = atob(pdfData);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `COTIZACION_${quotationInfo?.code || quotationCode || 'preview'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF descargado correctamente");
    } catch (err) {
      console.error("Error downloading PDF:", err);
      toast.error("Error al descargar el PDF");
    }
  };

  const handleClose = () => {
    if (isApiMode && onClose) {
      onClose();
    } else if (closePdfPreview) {
      closePdfPreview();
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  if (!shouldShow) return null;

  // Get the PDF source URL
  const pdfSrc = isApiMode && pdfData 
    ? `data:application/pdf;base64,${pdfData}`
    : pdfPreviewUrl;

  const displayCode = quotationInfo?.code || quotationCode || '';
  const displayName = quotationInfo?.name || '';

  return (
    <div 
      className="fixed inset-0 z-[150] bg-black/80 flex items-center justify-center p-4"
      onClick={handleClose}
      data-testid="pdf-preview-overlay"
    >
      <div 
        className={`relative bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col transition-all duration-200 ${
          isFullscreen 
            ? 'w-[95vw] h-[95vh]' 
            : 'w-full max-w-5xl h-[90vh]'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white flex-shrink-0">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            <div>
              <span className="font-medium">Vista Previa - Cotización {displayCode}</span>
              {displayName && (
                <span className="text-blue-200 text-sm ml-2">| {displayName}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isApiMode && !loading && !error && (
              <Button
                size="sm"
                variant="ghost"
                onClick={fetchPDFPreview}
                className="text-white hover:bg-white/20"
                title="Regenerar PDF"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="text-white hover:bg-white/20"
              title={isFullscreen ? "Reducir" : "Pantalla completa"}
            >
              {isFullscreen ? (
                <Minimize2 className="w-4 h-4" />
              ) : (
                <Maximize2 className="w-4 h-4" />
              )}
            </Button>
            <Button
              size="sm"
              onClick={isApiMode ? handleApiDownload : handleDownloadPDF}
              className="bg-white text-blue-700 hover:bg-blue-50"
              disabled={isApiMode && (loading || !pdfData)}
              data-testid="download-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Descargar PDF
            </Button>
            <button
              onClick={handleClose}
              className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
              data-testid="close-preview-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* PDF Viewer */}
          <div className="flex-1 overflow-hidden bg-slate-200 relative">
            {isApiMode && loading ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-white">
                <Loader2 className="w-12 h-12 text-blue-600 animate-spin mb-4" />
                <p className="text-slate-600 font-medium">Generando preview...</p>
                <p className="text-sm text-slate-400 mt-1">Esto puede tomar unos segundos</p>
              </div>
            ) : isApiMode && error ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-white">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
                <p className="text-slate-800 font-medium mb-2">Error al generar preview</p>
                <p className="text-sm text-slate-500 text-center max-w-md mb-4">{error}</p>
                <Button onClick={fetchPDFPreview} variant="outline">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reintentar
                </Button>
              </div>
            ) : pdfSrc ? (
              <iframe
                src={pdfSrc}
                className="w-full h-full border-0"
                title="PDF Preview"
              />
            ) : null}
          </div>

          {/* Sidebar with info (only in API mode) */}
          {isApiMode && !loading && !error && quotationInfo && (
            <div className="w-56 bg-white border-l p-4 flex flex-col flex-shrink-0">
              <h3 className="font-semibold text-slate-800 mb-4 text-sm">Resumen</h3>
              
              <div className="space-y-3 flex-1 text-sm">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Código</p>
                  <p className="font-mono font-medium text-blue-600">{quotationInfo.code}</p>
                </div>
                
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Cliente</p>
                  <p className="font-medium text-slate-800 truncate" title={quotationInfo.client_company}>
                    {quotationInfo.client_company || '-'}
                  </p>
                </div>
                
                <div className="pt-3 border-t">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Items</p>
                  <p className="text-xl font-bold text-slate-800">{quotationInfo.items_count}</p>
                </div>
                
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Total m²</p>
                  <p className="font-semibold text-slate-800">
                    {quotationInfo.total_m2?.toFixed(2)} m²
                  </p>
                </div>
                
                <div className="pt-3 border-t">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Subtotal</p>
                  <p className="text-lg font-bold text-green-600">
                    {formatCurrency(quotationInfo.subtotal)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PDFPreviewModal;
